import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';

const T = {
  ink:    '#111',
  muted:  '#888',
  border: '#e5e5e5',
  bg:     '#f7f7f7',
  font:   "'Inter', 'Helvetica Neue', Helvetica, Arial, sans-serif",
};

const inputStyle = {
  width: '100%',
  border: `1.5px solid ${T.border}`,
  borderRadius: 10,
  padding: '11px 14px',
  fontSize: 14,
  boxSizing: 'border-box',
  fontFamily: T.font,
  color: T.ink,
  background: 'white',
  outline: 'none',
  transition: 'border-color 0.15s',
};

const IcoCheck = () => (
  <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="#555" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
    <polyline points="20 6 9 17 4 12"/>
  </svg>
);

export default function Login() {
  const { signIn } = useAuth();
  const navigate   = useNavigate();
  const [form, setForm]       = useState({ email: '', password: '' });
  const [error, setError]     = useState('');
  const [loading, setLoading] = useState(false);

  async function handleSubmit(e) {
    e.preventDefault();
    setLoading(true);
    setError('');
    const { error: err } = await signIn(form);
    if (err) {
      if (err.message?.toLowerCase().includes('email not confirmed')) {
        setError('E-Mail-Adresse noch nicht bestätigt. Bitte prüfe dein Postfach.');
      } else if (err.message?.toLowerCase().includes('invalid login credentials') || err.status === 400) {
        setError('E-Mail oder Passwort falsch. Bitte erneut versuchen.');
      } else {
        setError(`Fehler: ${err.message}`);
      }
      setLoading(false);
    } else {
      navigate('/dashboard');
    }
  }

  return (
    <div style={{ minHeight: '100vh', display: 'flex', fontFamily: T.font, background: T.bg }}>

      {/* ── Left panel — branding ── */}
      <div
        className="login-left-panel"
        style={{
          display: 'none',
          width: 460, background: T.ink, flexShrink: 0,
          flexDirection: 'column', justifyContent: 'space-between',
          padding: '52px 56px',
        }}
      >
        <div>
          {/* Logo */}
          <div style={{
            fontSize: 15, fontWeight: 900, letterSpacing: '2.5px',
            textTransform: 'uppercase', color: 'white', marginBottom: 60,
          }}>
            IMMO<span style={{ color: T.ink }}>HQ</span>
          </div>

          {/* Eyebrow */}
          <div style={{
            display: 'inline-flex', alignItems: 'center', gap: 6,
            background: 'rgba(255,255,255,0.08)', border: '1px solid rgba(255,255,255,0.2)',
            borderRadius: 99, padding: '4px 12px', marginBottom: 28,
          }}>
            <div style={{ width: 6, height: 6, borderRadius: '50%', background: '#555' }} />
            <span style={{
              fontSize: 11, fontWeight: 700, letterSpacing: '0.8px',
              textTransform: 'uppercase', color: 'rgba(255,255,255,0.7)',
            }}>
              Immobilienverwaltung 2026
            </span>
          </div>

          <h2 style={{
            fontSize: 38, fontWeight: 900, letterSpacing: '-2px',
            lineHeight: 1.1, color: 'white', margin: '0 0 20px',
          }}>
            Immobilien verwalten —<br />
            <span style={{ color: '#555' }}>ohne Chaos.</span>
          </h2>

          <p style={{
            fontSize: 14, color: 'rgba(255,255,255,0.45)',
            lineHeight: 1.75, margin: '0 0 44px',
          }}>
            Mieten tracken, Mahnungen automatisch versenden,
            Dokumente organisieren. Alles in einer Plattform.
          </p>

          {/* Trust signals */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: 13 }}>
            {[
              'Keine Kreditkarte erforderlich',
              'DSGVO-konform · Server in Deutschland',
              'Für private Vermieter & Hausverwaltungen',
            ].map(item => (
              <div key={item} style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                <div style={{
                  width: 18, height: 18, borderRadius: '50%',
                  background: 'rgba(0,0,0,0.05)', border: '1px solid rgba(0,0,0,0.1)',
                  display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
                }}>
                  <IcoCheck />
                </div>
                <span style={{ fontSize: 13, color: 'rgba(255,255,255,0.5)' }}>{item}</span>
              </div>
            ))}
          </div>
        </div>

        <div style={{ fontSize: 11, color: 'rgba(255,255,255,0.2)', letterSpacing: '0.3px' }}>
          © {new Date().getFullYear()} ImmoHQ
        </div>
      </div>

      {/* ── Right panel — form ── */}
      <div style={{
        flex: 1, display: 'flex', alignItems: 'center',
        justifyContent: 'center', padding: '40px 24px',
      }}>
        <div style={{ width: '100%', maxWidth: 400 }}>

          {/* Logo (visible when left panel is hidden) */}
          <div style={{ textAlign: 'center', marginBottom: 40 }}>
            <Link to="/" style={{ textDecoration: 'none' }}>
              <span style={{
                fontSize: 15, fontWeight: 900, letterSpacing: '2.5px',
                textTransform: 'uppercase', color: T.ink,
              }}>
                IMMO<span style={{ color: T.ink }}>HQ</span>
              </span>
            </Link>
          </div>

          {/* Card */}
          <div style={{
            background: 'white',
            border: `1.5px solid ${T.border}`,
            borderRadius: 16,
            padding: '36px 36px 32px',
            boxShadow: '0 4px 24px rgba(0,0,0,0.06)',
          }}>
            <h1 style={{
              fontSize: 22, fontWeight: 800, letterSpacing: '-0.6px',
              color: T.ink, margin: '0 0 6px',
            }}>
              Willkommen zurück
            </h1>
            <p style={{ fontSize: 13, color: T.muted, margin: '0 0 28px', lineHeight: 1.5 }}>
              Melde dich mit deinem ImmoHQ-Konto an.
            </p>

            <form onSubmit={handleSubmit}>
              <div style={{ marginBottom: 16 }}>
                <label style={{
                  display: 'block', fontSize: 11, fontWeight: 700,
                  letterSpacing: '0.5px', textTransform: 'uppercase',
                  color: T.muted, marginBottom: 7,
                }}>
                  E-Mail-Adresse
                </label>
                <input
                  type="email" required
                  value={form.email}
                  onChange={e => setForm({ ...form, email: e.target.value })}
                  placeholder="name@firma.de"
                  style={inputStyle}
                  onFocus={e => e.target.style.borderColor = T.ink}
                  onBlur={e => e.target.style.borderColor = T.border}
                  autoComplete="email"
                />
              </div>

              <div style={{ marginBottom: 8 }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 7 }}>
                  <label style={{
                    fontSize: 11, fontWeight: 700, letterSpacing: '0.5px',
                    textTransform: 'uppercase', color: T.muted,
                  }}>
                    Passwort
                  </label>
                  <Link to="/forgot-password" style={{
                    fontSize: 12, color: T.ink,
                    textDecoration: 'none', fontWeight: 500,
                  }}>
                    Vergessen?
                  </Link>
                </div>
                <input
                  type="password" required
                  value={form.password}
                  onChange={e => setForm({ ...form, password: e.target.value })}
                  placeholder="••••••••"
                  style={inputStyle}
                  onFocus={e => e.target.style.borderColor = T.ink}
                  onBlur={e => e.target.style.borderColor = T.border}
                  autoComplete="current-password"
                />
              </div>

              {error && (
                <div style={{
                  border: '1px solid #fca5a5', background: '#fef2f2',
                  borderRadius: 8, padding: '10px 14px',
                  fontSize: 12, color: '#b91c1c',
                  marginTop: 14, marginBottom: 4, lineHeight: 1.5,
                }}>
                  {error}
                </div>
              )}

              <button
                type="submit" disabled={loading}
                style={{
                  width: '100%', marginTop: 20,
                  background: loading ? T.muted : T.ink,
                  color: 'white', border: 'none',
                  borderRadius: 10, padding: '13px',
                  fontSize: 13, fontWeight: 700, letterSpacing: '0.2px',
                  cursor: loading ? 'not-allowed' : 'pointer',
                  fontFamily: T.font,
                  
                  transition: 'background 0.15s, transform 0.1s, box-shadow 0.15s',
                }}
                onMouseEnter={e => { if (!loading) { e.currentTarget.style.background = '#000'; e.currentTarget.style.transform = 'translateY(-1px)'; } }}
                onMouseLeave={e => { if (!loading) { e.currentTarget.style.background = T.ink; e.currentTarget.style.transform = 'translateY(0)'; } }}
              >
                {loading ? 'Wird angemeldet…' : 'Anmelden →'}
              </button>
            </form>

            <div style={{
              borderTop: `1px solid ${T.border}`,
              marginTop: 24, paddingTop: 20, textAlign: 'center',
            }}>
              <span style={{ fontSize: 13, color: T.muted }}>Noch kein Konto? </span>
              <Link to="/register" style={{
                fontSize: 13, color: T.ink,
                fontWeight: 700, textDecoration: 'none',
              }}>
                Kostenlos registrieren →
              </Link>
            </div>
          </div>

          <div style={{ textAlign: 'center', marginTop: 20 }}>
            <Link to="/" style={{ fontSize: 12, color: T.muted, textDecoration: 'none' }}>
              ← Zurück zur Startseite
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
}
